import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  deliveryCountsSchema,
  deliveryDetailSchema,
  deliveryIdSchema,
  deliveryListSchema,
  type DeliveryCounts,
  type DeliveryDetail,
  type DeliveryFilters,
  type DeliveryList,
  type UpdateDeliveryInput,
} from '../schemas';
import { useToken } from './use-token';

/**
 * La file d'attente du cabinet : ce qui va partir, ce qui est parti, ce qui n'a
 * pas pu partir.
 *
 * Paginée par curseur : la liste bouge sous les yeux (un message part, un autre
 * est annulé), et un numéro de page ferait sauter ou répéter des lignes.
 */
export function useDeliveries(filters: DeliveryFilters) {
  const api = useApi();
  const token = useToken();

  return useInfiniteQuery({
    queryKey: queryKeys.deliveries.list(filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ view: filters.view });
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.types && filters.types.length > 0) params.set('types', filters.types.join(','));
      if (filters.agendas && filters.agendas.length > 0) {
        params.set('agendas', filters.agendas.join(','));
      }
      if (filters.q) params.set('q', filters.q);
      if (pageParam) params.set('cursor', pageParam);

      return api.get<DeliveryList>(`/deliveries?${params}`, { schema: deliveryListSchema });
    },
    getNextPageParam: (last) => last.nextCursor,
    enabled: token !== null,
    // Un message part à l'heure dite sans que personne n'ait rien fait : la
    // liste se relit toute seule.
    refetchInterval: 30_000,
  });
}

/** Les trois compteurs des onglets. */
export function useDeliveryCounts() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.deliveries.counts(),
    queryFn: () => api.get<DeliveryCounts>('/deliveries/counts', { schema: deliveryCountsSchema }),
    enabled: token !== null,
    refetchInterval: 30_000,
  });
}

/** Une ligne avec son message rendu et son journal. `id` nul : rien n'est chargé. */
export function useDelivery(id: string | null) {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.deliveries.detail(id ?? ''),
    queryFn: () => api.get<DeliveryDetail>(`/deliveries/${id}`, { schema: deliveryDetailSchema }),
    enabled: token !== null && id !== null,
  });
}

function useInvalidateDeliveries() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.deliveries.all() });
}

/**
 * Arrête un envoi qui n'est pas encore parti. Refus en `409` avec un `code` :
 * `already_sending`, `already_sent`, `not_cancellable`.
 */
export function useCancelDelivery() {
  const api = useApi();
  const invalidate = useInvalidateDeliveries();

  return useMutation({
    mutationFn: (id: string) => api.post(`/deliveries/${id}/cancel`),
    onSettled: invalidate,
  });
}

/**
 * Remet en file un envoi annulé. Refus en `409` : `not_reschedulable`,
 * `version_conflict`.
 */
export function useRescheduleDelivery() {
  const api = useApi();
  const invalidate = useInvalidateDeliveries();

  return useMutation({
    mutationFn: (id: string) => api.post(`/deliveries/${id}/reschedule`),
    onSettled: invalidate,
  });
}

/**
 * Envoie tout de suite un message « après » programmé plus tard. Refus en `409` :
 * `already_sending`, `already_sent`, `not_sendable_now`.
 */
export function useSendDeliveryNow() {
  const api = useApi();
  const invalidate = useInvalidateDeliveries();

  return useMutation({
    mutationFn: (id: string) => api.post(`/deliveries/${id}/send-now`),
    onSettled: invalidate,
  });
}

/**
 * Retouche le texte d'un envoi. Refus en `409` : `edit_window_closed` (moins de
 * 5 minutes avant l'envoi), `not_editable`, `version_conflict`.
 */
export function useUpdateDelivery() {
  const api = useApi();
  const invalidate = useInvalidateDeliveries();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateDeliveryInput & { id: string }) =>
      api.patch(`/deliveries/${id}`, input),
    onSettled: invalidate,
  });
}

/** Renvoie un message en échec (avis Google et consignes post-op), à l'adresse donnée. */
/**
 * « Coup de cœur » : le lien du mail d'avis envoie ce patient directement sur la fiche Google.
 * Refus en `400` si l'option est coupée ou si ce n'est pas un message d'avis Google.
 */
export function useHeartDelivery() {
  const api = useApi();
  const invalidate = useInvalidateDeliveries();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.post(`/deliveries/${id}/heart`, { enabled }),
    onSettled: invalidate,
  });
}

export function useResendDelivery() {
  const api = useApi();
  const invalidate = useInvalidateDeliveries();

  return useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) =>
      api.post<{ id: string }>(`/deliveries/${id}/resend`, { email }, { schema: deliveryIdSchema }),
    onSettled: invalidate,
  });
}
